import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const overlayStyles = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  animation: 'fadeIn 200ms ease-out'
};

const contentStyles = {
  position: 'fixed',
  left: '50%',
  top: '50%',
  zIndex: 50,
  display: 'grid',
  width: '100%',
  maxWidth: '512px',
  transform: 'translate(-50%, -50%)',
  gap: '16px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  padding: '24px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  animation: 'dialogOpen 200ms ease-out'
};

const closeButtonStyles = {
  position: 'absolute',
  right: '16px',
  top: '16px',
  borderRadius: '4px',
  opacity: 0.7,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  transition: 'opacity 200ms',
  outline: 'none'
};

const DialogOverlay = React.forwardRef(({ style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ ...overlayStyles, ...style }}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(({ style, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{ ...contentStyles, ...style }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close 
        style={closeButtonStyles}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
      >
        <X style={{ height: '16px', width: '16px' }} />
        <span style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}>Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ style, ...props }) => (
  <div 
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      textAlign: 'left',
      ...style
    }} 
    {...props} 
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ style, ...props }) => (
  <div 
    style={{
      display: 'flex',
      flexDirection: 'column-reverse',
      gap: '8px',
      '@media (min-width: 640px)': {
        flexDirection: 'row',
        justifyContent: 'flex-end'
      },
      ...style
    }} 
    {...props} 
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(({ style, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    style={{
      fontSize: '18px',
      fontWeight: '600',
      lineHeight: '1',
      letterSpacing: '-0.025em',
      ...style
    }}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({ style, ...props }, ref) => (
  <DialogPrimitive.Description 
    ref={ref} 
    style={{
      fontSize: '14px',
      color: '#9ca3af',
      ...style
    }} 
    {...props} 
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// Add keyframe animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes dialogOpen {
    from {
      opacity: 0;
      transform: translate(-50%, -48%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  @keyframes dialogClose {
    from {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -48%) scale(0.95);
    }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('dialog-animations')) {
  styleSheet.id = 'dialog-animations';
  document.head.appendChild(styleSheet);
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};